const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 60 * 60 * 1000],
  ['month', 30 * 24 * 60 * 60 * 1000],
  ['week', 7 * 24 * 60 * 60 * 1000],
  ['day', 24 * 60 * 60 * 1000],
  ['hour', 60 * 60 * 1000],
  ['minute', 60 * 1000],
]

const moneyFormatter = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' })
const relativeFormatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
const compactFormatter = new Intl.NumberFormat(undefined, { notation: 'compact' })

/** 1763.87 -> "1.763,87 €". Contract values, revenue, invoice totals. */
export function formatMoney(value: number): string {
  return moneyFormatter.format(value || 0)
}

const priceFormatters = new Map<string, Intl.NumberFormat>()

/**
 * { amount: "990.00", currency: "USD" } -> "990,00 $". A server price in its own
 * currency. Structural, not `Money`: `lib/` must not import from features.
 * `Number()` is for display only; nothing here does arithmetic on the amount.
 */
export function formatPrice(price: { amount: string; currency: string }): string {
  let formatter = priceFormatters.get(price.currency)
  if (!formatter) {
    formatter = new Intl.NumberFormat('de-DE', { style: 'currency', currency: price.currency })
    priceFormatters.set(price.currency, formatter)
  }
  return formatter.format(Number(price.amount))
}

/** "3 hours ago" from an ISO timestamp. */
export function formatRelativeTime(iso: string, now: number = Date.now()): string {
  const delta = new Date(iso).getTime() - now

  for (const [unit, ms] of RELATIVE_UNITS) {
    if (Math.abs(delta) >= ms) {
      return relativeFormatter.format(Math.round(delta / ms), unit)
    }
  }
  return relativeFormatter.format(Math.round(delta / 1000), 'second')
}

const dayFormatter = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})

/**
 * An ISO date as a day a person recognises: "Heute", "Morgen", "Gestern" or
 * "12.03.2026". Due dates read faster this way than as a bare date.
 */
export function formatCalendarDay(iso: string, now: Date = new Date()): string {
  const date = new Date(`${iso}T12:00:00`)
  if (Number.isNaN(date.getTime())) return iso

  const midday = new Date(now)
  midday.setHours(12, 0, 0, 0)
  const days = Math.round((date.getTime() - midday.getTime()) / 86_400_000)

  if (days === 0) return 'Heute'
  if (days === 1) return 'Morgen'
  if (days === -1) return 'Gestern'
  return dayFormatter.format(date)
}

/**
 * "2026-10-28" -> "28.10.2026". For a date-only value that already names a day in the studio's
 * calendar: read and printed at the same local noon, it never shifts to a neighbouring day.
 */
export function formatIsoDate(iso: string): string {
  const date = new Date(`${iso}T12:00:00`)
  return Number.isNaN(date.getTime()) ? iso : dayFormatter.format(date)
}

/** 12400 -> "12K". Used for like and follower counts. */
export function formatCompactNumber(value: number): string {
  return compactFormatter.format(value)
}
